import React, { PureComponent } from 'react'
import { withAnalytics, WithAnalyticsProps } from '#src/containers/Analytics'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import { Grid, GridItem } from '@deal/components'
import { generateGrid } from '#src/services/grid'
import { DepartmentType } from '#src/generated/graphql'
import { getSellableEcommerceProperties } from '#src/services/tracking'
import uuidv4 from 'uuid/v4'
import SellableMetaData from '#src/components/SellableMetaData'
import CategoryChatWidget from '#src/containers/CategoryChatWidget'
import Footer from '#src/components/Footer'
import Amenities from '../../../components/CharterAmenities'
import BackButton from '../../../components/BackButton'
import YachtBooking from '../../../components/YachtBooking'
import Description from '../../../components/Description'
import Expert from '../../../components/Expert'
import FAQ from '../../../components/FAQ'
import Features from '../../../components/Features'
import ImageCarousel from '../../../components/ImageCarousel'
import Map from '../../../components/Map'
import RatesTable from '../../../components/RatesTable'
import SectionHeader from '../../../components/SectionHeader'
import Specifications from '../../../components/Specifications'
import Title from '../../../components/Title'
import styles from './styles.css'
import { ExperienceBookingProps } from '../../../'

class ExperienceBookingPage extends PureComponent<
  ExperienceBookingProps & WithAnalyticsProps & RouteComponentProps<{}>
> {
  private activityId?: string

  constructor(props: ExperienceBookingProps & WithAnalyticsProps & RouteComponentProps<{}>) {
    super(props)

    this.state = {
      isImageSliderExpanded: false
    }

    this.getTrackingProperties = this.getTrackingProperties.bind(this)
  }

  public getTrackingProperties() {
    return Object.assign(
      this.activityId
        ? {
            activity_id: this.activityId
          }
        : {},
      getSellableEcommerceProperties(this.props.sellable)
    )
  }

  public componentDidMount() {
    if (this.props.analytics) {
      this.activityId = uuidv4()

      this.props.analytics.productViewed(this.getTrackingProperties())
    }
  }

  public componentWillUnmount() {
    if (this.props.analytics && this.activityId) {
      this.props.analytics.productViewEnded(this.getTrackingProperties())
    }
  }

  public componentDidUpdate(prevProps: ExperienceBookingProps) {
    if (this.props.analytics && this.props.sellable.id !== prevProps.sellable.id) {
      if (this.activityId) {
        this.props.analytics.productViewEnded(this.getTrackingProperties())
      }

      this.activityId = uuidv4()

      this.props.analytics.productViewed(this.getTrackingProperties())
    }
  }

  public render() {
    const { sellable, expertCuratedOrder } = this.props
    const sellableCategory = sellable.categories[0]

    const defaultTopSection = generateGrid([
      {
        title: 2
      },
      {
        features: 2
      },
      {
        booking: 2
      },
      {
        descriptionHeader: 2
      },
      {
        description: 2
      },
      {
        amenitiesHeader: 2
      },
      {
        amenities: 2
      },
      {
        ratesHeader: 2
      },
      {
        rates: 2
      }
    ])

    const mdTopSection = generateGrid([
      {
        title: 8,
        booking: 4
      },
      {
        features: 8,
        booking: 4
      },
      {
        descriptionHeader: 2,
        description: 6,
        booking: 4
      },
      {
        amenitiesHeader: 2,
        amenities: 6,
        booking: 4
      },
      {
        ratesHeader: 2,
        rates: 6,
        booking: 4
      }
    ])

    return (
      <>
        <SellableMetaData sellable={sellable} />
        <div className={styles.content}>
          <CategoryChatWidget categoryId={sellableCategory.id} className={styles.chatHead} />

          <div className={styles.imageCarousel}>
            <BackButton label="Back to search" />

            <ImageCarousel images={sellable.images} />
          </div>

          <Grid areas={defaultTopSection} areasMd={mdTopSection} className={styles.experience}>
            {({
              Title: TitleArea,
              Features: FeaturesArea,
              DescriptionHeader,
              Description: DescriptionArea,
              AmenitiesHeader,
              Amenities: AmenitiesArea,

              Rates,
              RatesHeader,
              Booking: BookingArea
            }) => (
              <>
                <TitleArea>
                  <Title
                    title={sellable.title}
                    subTitle={
                      expertCuratedOrder.serviceLocation &&
                      expertCuratedOrder.serviceLocation.displayTitle
                    }
                  />
                </TitleArea>

                <FeaturesArea>
                  <Features sellable={sellable} />
                </FeaturesArea>

                {sellable.descriptionText && (
                  <>
                    <DescriptionHeader>
                      <SectionHeader header="About" />
                    </DescriptionHeader>

                    <DescriptionArea>
                      <Description descriptionText={sellable.descriptionText} />
                    </DescriptionArea>
                  </>
                )}

                {sellable.charterAmenities && (
                  <>
                    <AmenitiesHeader>
                      <SectionHeader header="Amenities" />
                    </AmenitiesHeader>

                    <AmenitiesArea>
                      <Amenities sellable={sellable} />
                    </AmenitiesArea>
                  </>
                )}

                <RatesHeader>
                  <SectionHeader header="Rates" withBorder />
                </RatesHeader>

                <Rates>
                  <RatesTable sellable={sellable} />
                </Rates>

                <BookingArea className={styles.bookingContainer}>
                  <YachtBooking expertCuratedOrder={expertCuratedOrder} />
                </BookingArea>
              </>
            )}
          </Grid>

          <Expert
            header={
              <>
                Make it an <em>unforgettable</em> experience
              </>
            }
            body={
              <>
                From customized amenities to world class catering, our experts are ready to plan
                your perfect day.
              </>
            }
            cta="Connect with an expert"
            categorySlug={sellableCategory.slug}
            departmentType={DepartmentType.YACHT_CHARTERS}
          />

          <Grid>
            <GridItem col={2} mdCol={2}>
              <SectionHeader header="Specifications" />
            </GridItem>
            <GridItem col={2} mdCol={6}>
              <Specifications specifications={sellable.specifications} />
            </GridItem>
          </Grid>

          <Grid>
            <GridItem col={2} mdCol={2}>
              <SectionHeader header="Need to Know" />
            </GridItem>

            <GridItem col={2} mdCol={6}>
              <FAQ />
            </GridItem>
          </Grid>

          {sellable.serviceLocation &&
            sellable.serviceLocation.displayTitle && (
              <Grid className={styles.location}>
                <GridItem col={2} mdCol={2}>
                  <SectionHeader header="Location" />
                </GridItem>
                <GridItem col={2} mdCol={6}>
                  <Map location={sellable.serviceLocation.displayTitle} />
                </GridItem>
              </Grid>
            )}

          <Footer className={styles.footer} />
        </div>
      </>
    )
  }
}

export default withRouter(withAnalytics(ExperienceBookingPage))
